import dayjs from "dayjs";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  ComposedChart,
  Customized,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

import {
  FitbitSleepStage,
  IFitbitSleepSession,
  IFitbitSleepStageSegment,
} from "../../../../../../@types/Fitbit";
import { StagesChartWrapper } from "./FitbitSleepStagesChart.styles";

type StageKey = "awake" | "rem" | "light" | "deep";

const STAGE_TO_INDEX: Record<StageKey, number> = {
  awake: 0,
  rem: 1,
  light: 2,
  deep: 3,
};

const STAGE_LABELS: StageKey[] = ["awake", "rem", "light", "deep"];
// EN-5191: a factory, not a module constant. A constant is evaluated at import time, before
// i18next is initialised, so every label would come back undefined.
const buildStageDisplay = (t: TFunction): Record<StageKey, string> => ({
  awake: t("patientPage_measurementModal_sleep_awake"),
  rem: t("patientPage_measurementModal_sleep_rem"),
  light: t("patientPage_measurementModal_sleep_light"),
  deep: t("patientPage_measurementModal_sleep_deep"),
});

const STAGE_COLOR: Record<StageKey, string> = {
  awake: "#EB5A7B",
  rem: "#A8C6FF",
  light: "#6697F4",
  deep: "#58C488",
};

const STROKE_WIDTH = 9;
const TRANSITION_STROKE_WIDTH = 2;

// Map API stage values to the four-band rendering domain.
// CLASSIC stages (Asleep/Restless) are folded into Light so the path stays continuous;
// callers should not render the chart when only CLASSIC data is available.
function toStageKey(stage: FitbitSleepStage): StageKey | null {
  switch (stage) {
    case "Awake":
      return "awake";
    case "Rem":
      return "rem";
    case "Light":
    case "Restless":
      return "light";
    case "Deep":
      return "deep";
    case "Asleep":
      return "light";
    default:
      return null;
  }
}

// Each stage occupies a horizontal band on the Y axis, centred on its index.
const STAGE_BANDS: Array<{
  stage: StageKey;
  min: number;
  max: number;
}> = [
  { stage: "awake", min: -0.5, max: 0.5 },
  { stage: "rem", min: 0.5, max: 1.5 },
  { stage: "light", min: 1.5, max: 2.5 },
  { stage: "deep", min: 2.5, max: 3.5 },
];

function sliceTransitionByBand(
  fromIdx: number,
  toIdx: number
): Array<{ from: number; to: number; color: string }> {
  if (fromIdx === toIdx) return [];

  const lo = Math.min(fromIdx, toIdx);
  const hi = Math.max(fromIdx, toIdx);
  const slices: Array<{ from: number; to: number; color: string }> = [];

  STAGE_BANDS.forEach((band) => {
    const sliceLo = Math.max(lo, band.min);
    const sliceHi = Math.min(hi, band.max);
    if (sliceLo < sliceHi) {
      slices.push({
        from: sliceLo,
        to: sliceHi,
        color: STAGE_COLOR[band.stage],
      });
    }
  });

  return slices;
}

type RenderableSegment = {
  startTime: string;
  endTime: string;
  stageKey: StageKey;
};

type StagesPathProps = {
  mainSegments: RenderableSegment[];
  shortAwakeSegments: IFitbitSleepStageSegment[];
  xAxisMap?: Record<string, any>;
  yAxisMap?: Record<string, any>;
};

function StagesPath({
  mainSegments,
  shortAwakeSegments,
  xAxisMap,
  yAxisMap,
}: StagesPathProps) {
  if (!xAxisMap || !yAxisMap) return null;

  const xAxis = Object.values(xAxisMap)[0] as any;
  const yAxis = Object.values(yAxisMap)[0] as any;
  const xScale = xAxis?.scale;
  const yScale = yAxis?.scale;
  if (!xScale || !yScale) return null;

  // The y position where short-period awake marks sit (just below the chart top).
  const awakeY = yScale(STAGE_TO_INDEX.awake);

  return (
    <g>
      {mainSegments.map((seg, i) => {
        const x1 = xScale(dayjs(seg.startTime).valueOf());
        const x2 = xScale(dayjs(seg.endTime).valueOf());
        const stageIdx = STAGE_TO_INDEX[seg.stageKey];
        const y = yScale(stageIdx);
        const color = STAGE_COLOR[seg.stageKey];
        const next = mainSegments[i + 1];

        return (
          <g key={`main-${seg.startTime}-${i}`}>
            <line
              x1={x1}
              y1={y}
              x2={x2}
              y2={y}
              stroke={color}
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
            />
            {next
              ? sliceTransitionByBand(
                  stageIdx,
                  STAGE_TO_INDEX[next.stageKey]
                ).map((slice) => (
                  <line
                    key={`trans-${seg.startTime}-${i}-${slice.from}`}
                    x1={x2}
                    y1={yScale(slice.from)}
                    x2={x2}
                    y2={yScale(slice.to)}
                    stroke={slice.color}
                    strokeWidth={TRANSITION_STROKE_WIDTH}
                    strokeLinecap="butt"
                  />
                ))
              : null}
          </g>
        );
      })}

      {shortAwakeSegments.map((seg, i) => {
        const startMs = dayjs(seg.startTime).valueOf();
        const endMs = dayjs(seg.endTime).valueOf();
        const x1 = xScale(startMs);
        const x2 = xScale(endMs);
        return (
          <line
            key={`short-${seg.startTime}-${i}`}
            x1={x1}
            y1={awakeY}
            x2={x2}
            y2={awakeY}
            stroke={STAGE_COLOR.awake}
            strokeWidth={3}
            strokeLinecap="round"
          />
        );
      })}
    </g>
  );
}

type FitbitSleepStagesChartProps = {
  session: IFitbitSleepSession;
  width?: number | string;
  height?: number | string;
};

export function FitbitSleepStagesChart({
  session,
  width = "100%",
  height = 240,
}: FitbitSleepStagesChartProps) {
  const { t } = useTranslation();
  const stageDisplay = useMemo(() => buildStageDisplay(t), [t]);

  const { mainSegments, shortAwakeSegments } = useMemo(() => {
    const main: RenderableSegment[] = [];
    const shortAwake: IFitbitSleepStageSegment[] = [];

    session.segments.forEach((seg) => {
      if (seg.isShortPeriod) {
        if (seg.stage === "Awake") shortAwake.push(seg);
        return;
      }
      const stageKey = toStageKey(seg.stage);
      if (stageKey) {
        main.push({
          startTime: seg.startTime,
          endTime: seg.endTime,
          stageKey,
        });
      }
    });

    return { mainSegments: main, shortAwakeSegments: shortAwake };
  }, [session.segments]);

  const { domain, ticks } = useMemo(() => {
    if (mainSegments.length === 0) {
      const sessionStart = dayjs(session.startTime).valueOf();
      const sessionEnd = dayjs(session.endTime).valueOf();
      return {
        domain: [sessionStart, sessionEnd] as [number, number],
        ticks: [sessionStart, sessionEnd],
      };
    }
    const start = dayjs(mainSegments[0].startTime).valueOf();
    const end = dayjs(mainSegments[mainSegments.length - 1].endTime).valueOf();
    const span = end - start;
    const tickList = span > 0 ? [start, start + span / 2, end] : [start];
    return {
      domain: [start, end] as [number, number],
      ticks: tickList,
    };
  }, [mainSegments, session.startTime, session.endTime]);

  const anchorData = useMemo(
    () => [
      { timeValue: domain[0], y: 0 },
      { timeValue: domain[1], y: 3 },
    ],
    [domain]
  );

  return (
    <StagesChartWrapper>
      <ResponsiveContainer width={width} height={height}>
        <ComposedChart
          data={anchorData}
          margin={{ top: 12, right: 16, left: 24, bottom: 8 }}
        >
          {/* No automatic grid lines; we draw them at half-integer Y positions
              via ReferenceLine so they sit between stage bands (not on the labels). */}
          <CartesianGrid horizontal={false} vertical={false} />

          <XAxis
            dataKey="timeValue"
            type="number"
            scale="time"
            domain={domain}
            ticks={ticks}
            tickFormatter={(value) => dayjs(Number(value)).format("HH:mm")}
            tick={{ fill: "#727272", fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            tickMargin={12}
            interval={0}
          />

          {/* Domain extends to 4.5 to leave an empty band below Deep. */}
          <YAxis
            type="number"
            domain={[-0.5, 4.5]}
            ticks={[0, 1, 2, 3]}
            tickFormatter={(value) =>
              stageDisplay[STAGE_LABELS[Number(value)]] ?? ""
            }
            tick={{ fill: "#727272", fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            reversed
            orientation="right"
            width={56}
          />

          {[-0.5, 0.5, 1.5, 2.5, 3.5, 4.5].map((y) => (
            <ReferenceLine
              key={y}
              y={y}
              stroke="#D8D8D8"
              strokeDasharray="4 4"
            />
          ))}

          <Line
            dataKey="y"
            stroke="transparent"
            dot={false}
            isAnimationActive={false}
          />

          <Customized
            component={
              <StagesPath
                mainSegments={mainSegments}
                shortAwakeSegments={shortAwakeSegments}
              />
            }
          />
        </ComposedChart>
      </ResponsiveContainer>
    </StagesChartWrapper>
  );
}
